import { computed, ref } from "vue";

/**
 * Vertical swipe-to-dismiss for a bottom-anchored panel.
 *
 * Thresholds are deliberately NOT borrowed from MediaViewerModal. That component
 * swipes horizontally to page between items and dismisses only on Escape, so its
 * 50px distance is tuned for paging — on a 70dvh sheet that is roughly 7% of the
 * travel, close enough to an accidental drag to fire on one.
 */
export interface SwipeDismissOptions {
  /** Fraction of the panel's own height that must be dragged. */
  distanceRatio?: number;
  /** Floor for the distance threshold, so short panels still need a real drag. */
  minDistancePx?: number;
  /** Downward speed that dismisses regardless of distance. */
  velocityThreshold?: number;
  /** Drag is ignored until it exceeds this, so taps and scrolls are unaffected. */
  activationPx?: number;
}

const DEFAULTS = {
  distanceRatio: 0.25,
  minDistancePx: 80,
  velocityThreshold: 0.5,
  activationPx: 10
} satisfies Required<SwipeDismissOptions>;

// Sampling window for velocity. Matches MediaViewerModal's cadence — that part
// is a general touch-tracking concern, not a paging-specific tuning.
const VELOCITY_SAMPLE_MS = 50;

export function useSwipeDismiss(
  onDismiss: () => void,
  options: SwipeDismissOptions = {}
) {
  const opts = { ...DEFAULTS, ...options };

  const dragOffsetY = ref(0);
  const isDragging = ref(false);

  let startY: number | null = null;
  let startX = 0;
  let lastY = 0;
  let lastMoveAt = 0;
  let velocityY = 0;
  let panelHeight = 0;

  // Only a drag that begins on a non-scrolled region should move the sheet;
  // otherwise a flick inside the body would drag the panel instead of scrolling.
  const startedOnScrolledContent = (target: EventTarget | null): boolean => {
    let el = target as HTMLElement | null;
    while (el) {
      if (el.scrollHeight > el.clientHeight && el.scrollTop > 0) return true;
      el = el.parentElement;
    }
    return false;
  };

  const reset = () => {
    startY = null;
    isDragging.value = false;
    dragOffsetY.value = 0;
    velocityY = 0;
  };

  const onTouchStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) return reset();
    if (startedOnScrolledContent(event.target)) return;

    const touch = event.touches[0];
    startY = touch.clientY;
    startX = touch.clientX;
    lastY = touch.clientY;
    lastMoveAt = Date.now();
    velocityY = 0;
    panelHeight =
      (event.currentTarget as HTMLElement | null)?.getBoundingClientRect()
        .height ?? 0;
  };

  const onTouchMove = (event: TouchEvent) => {
    if (startY === null || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaY = touch.clientY - startY;
    const deltaX = touch.clientX - startX;

    // Downward and vertically dominant only. An upward drag or a horizontal
    // swipe belongs to the content, not the sheet.
    if (deltaY <= 0 || Math.abs(deltaY) <= Math.abs(deltaX)) return;
    if (deltaY <= opts.activationPx) return;

    isDragging.value = true;
    dragOffsetY.value = deltaY;

    const now = Date.now();
    const elapsed = now - lastMoveAt;
    if (elapsed >= VELOCITY_SAMPLE_MS) {
      velocityY = (touch.clientY - lastY) / elapsed;
      lastY = touch.clientY;
      lastMoveAt = now;
    }
  };

  const onTouchEnd = () => {
    if (startY === null) return;

    const travelled = dragOffsetY.value;
    const distanceThreshold = Math.max(
      opts.minDistancePx,
      panelHeight * opts.distanceRatio
    );
    const dismiss =
      travelled >= distanceThreshold || velocityY >= opts.velocityThreshold;

    reset();
    if (dismiss) onDismiss();
  };

  const dragStyle = computed(() =>
    dragOffsetY.value === 0
      ? {}
      : { transform: `translateY(${dragOffsetY.value}px)` }
  );

  return {
    dragOffsetY,
    isDragging,
    dragStyle,
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
}
